import React, { useEffect, useState } from 'react';
import { get } from 'lodash/fp';
import { Icon } from 'rsuite';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { GuestType } from '../../../../interfaces/guest';
import formatFullName from '../../../../helpers/formatFullName';
import { getClientColumnsByName } from '../../../../redux/selectors/client';
import guestAddressLine from '../../../../helpers/guestAddressLine';
import MainSettingsType from '../../../../interfaces/MainSettingsType';
import { VIEW_ADDRESS } from '../../../../constants/permissions/guest';
import ProfilePhoto from '../../../../components/guest/ProfilePhoto';
import {
  ContactNumber,
  DetailsItem,
  ItemLabel,
  ItemValue
} from '../../../../components/guest/ViewElements';
import UploadPhotoModal from '../../../../components/guest/UploadPhotoModal';
import RedLabel from '../../../../components/RedLabel';
import AccessControl from '../../../../components/AccessControl';
import { viewAccess } from '../../../../helpers/permission';
import { createFilename } from '../../../../helpers/guestProfilePhoto';
import { AppConfig } from '../../../../helpers/config';
import { awsS3presignedRequest } from '../../../../services/fbm-api';
import DeleteConfirmationModal from '../../../../components/DeleteConfirmationModal';
import { parseInt } from '../../../../helpers/utils';

const Section = styled.div`
  margin-top: -6px;
  vertical-align: top;
  display: inline-block;
`;

const ImageWrapper = styled.div`
  float: left;
  margin-right: 20px;
  position: relative;
  :hover {
    cursor: pointer;
  }
`;

const EditProfilePhotoIcon = styled(Icon)`
  right: 0;
  padding: 5px;
  position: absolute;
  background-color: #fff;
  border: 1px solid #ddd;
  border-bottom-left-radius: 5px;
  color: #3498ff;
`;

const DeleteProfilePhotoIcon = styled(Icon)`
  padding: 5px;
  position: absolute;
  background-color: #fff;
  border: 1px solid #ddd;
  border-bottom-right-radius: 5px;
  color: #f44336;
`;

const StyledItemLabel = styled(ItemLabel)`
  width: 75px;
  font-size: 12px;
  padding-top: 3px;
  margin-right: 10px;
`;

const ItemName = styled.h4`
  > span:first-child {
    color: #aaa;
    font-size: 80%;
  }
  > span:nth-child(2) {
    color: #666;
    font-size: 90%;
  }
`;

const MainDetails = ({ guest, settings, isSnapshot, ...props }) => {
  const columns = useSelector(getClientColumnsByName);
  const [showEditPhotoModal, setShowEditPhotoModal] = useState(false);
  const [showDeletePhotoModal, setShowDeletePhotoModal] = useState(false);
  const [hasProfilePhoto, setHasProfilePhoto] = useState(false);
  const [reloadProfilePhoto, setReloadProfilePhoto] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const guestId = get('id', guest).toString();
  const firstName = get('firstname', guest);
  const lastName = get('lastname', guest);
  const middleName = get('middlename', guest);
  const fullName = formatFullName([firstName, middleName, lastName]);
  const isHomeless = parseInt(get('homeless', guest)) === 1;
  const email = get('email', guest);
  const homePhone = get('phone', guest);
  const workPhone = get('workphone', guest);
  const cellPhone = get('cellphone', guest);
  const hasContactInfo = homePhone || workPhone || cellPhone;
  const { line1, line2 } = guestAddressLine(guest, true, false);

  const handleUploadComplete = randomVal => {
    setShowEditPhotoModal(false);
    setReloadProfilePhoto(randomVal);
  };

  const handleShowEditPhotoModal = () => {
    setShowEditPhotoModal(true);
  };

  const handleHideEditPhotoModal = () => {
    setShowEditPhotoModal(false);
  };

  const handleShowDeletePhotoModal = () => {
    setShowDeletePhotoModal(true);
  };

  const handleHideDeletePhotoModal = () => {
    setShowDeletePhotoModal(false);
  };

  const handleDeleteConfirm = () => {
    setIsDeleting(true);
    const filename = createFilename(guestId);
    const bucket = get('buckets.images', AppConfig);
    const body = { bucket, filename, method: 'delete' };
    awsS3presignedRequest({ body }).then(() => {
      setIsDeleting(false);
      setShowDeletePhotoModal(false);
      setReloadProfilePhoto(parseInt(guestId, 10));
    });
  };

  useEffect(
    () => () => {
      setShowEditPhotoModal(false);
      setShowDeletePhotoModal(false);
      setReloadProfilePhoto(null);
    },
    []
  );

  return (
    <div {...props} style={{ minHeight: 150 }}>
      <ImageWrapper>
        {!isSnapshot && (
          <EditProfilePhotoIcon
            icon="edit2"
            onClick={handleShowEditPhotoModal}
          />
        )}
        {!isSnapshot &&
          hasProfilePhoto && (
            <DeleteProfilePhotoIcon
              icon="close-circle"
              onClick={handleShowDeletePhotoModal}
            />
          )}
        <ProfilePhoto
          guestId={guestId}
          reload={reloadProfilePhoto}
          setHasProfilePhoto={setHasProfilePhoto}
        />
      </ImageWrapper>

      <Section>
        <ItemName>
          {fullName}
          &nbsp;&nbsp;&nbsp;
          <span className="divider">/</span>
          &nbsp;&nbsp;
          <span>{guestId} </span>
        </ItemName>
        {hasContactInfo && (
          <DetailsItem style={{ marginBottom: 5 }}>
            <StyledItemLabel>Contact #: </StyledItemLabel>
            {settings.guestShowHomePhone === '1' &&
              homePhone && (
                <ContactNumber>
                  <small>Home: </small>
                  {homePhone}
                </ContactNumber>
              )}
            {settings.guestShowWorkPhone === '1' &&
              workPhone && (
                <ContactNumber>
                  <small>Work: </small>
                  {workPhone}
                </ContactNumber>
              )}
            {settings.guestShowCellPhone === '1' &&
              cellPhone && (
                <ContactNumber>
                  <small>Cell: </small>
                  {cellPhone}
                </ContactNumber>
              )}
          </DetailsItem>
        )}
        {settings.guestRequireEmail !== '0' &&
          email && (
            <DetailsItem style={{ marginBottom: 5 }}>
              <StyledItemLabel>{get('email.label', columns)}</StyledItemLabel>
              <ItemValue>{get('email', guest)}</ItemValue>
            </DetailsItem>
          )}
        {isHomeless ? (
          <DetailsItem style={{ marginBottom: 5 }}>
            <StyledItemLabel>{get('homeless.label', columns)}</StyledItemLabel>
            <RedLabel>Yes</RedLabel>
          </DetailsItem>
        ) : (
          (line1 || line2) && (
            <DetailsItem style={{ marginBottom: 5 }}>
              <StyledItemLabel>Address: </StyledItemLabel>
              <ItemValue>
                <AccessControl allowOnlyFor={viewAccess(VIEW_ADDRESS)}>
                  {line1}
                </AccessControl>
                <div>{line2}</div>
              </ItemValue>
            </DetailsItem>
          )
        )}
      </Section>

      <UploadPhotoModal
        guestId={guestId}
        show={showEditPhotoModal}
        onHide={handleHideEditPhotoModal}
        onComplete={handleUploadComplete}
      />
      <DeleteConfirmationModal
        message="Are you sure you want to delete this image?"
        show={showDeletePhotoModal}
        onHide={handleHideDeletePhotoModal}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </div>
  );
};

MainDetails.propTypes = {
  guest: GuestType.isRequired,
  isSnapshot: PropTypes.bool,
  settings: MainSettingsType
};

MainDetails.defaultProps = {
  settings: {},
  isSnapshot: false
};

export default MainDetails;
